/**
 * CSRF Protection System - Prevents Cross-Site Request Forgery attacks
 * Implements double-submit cookie pattern and request origin validation
 */

export class CSRFProtection {
  static TOKEN_HEADER = 'X-CSRF-Token';
  static TOKEN_COOKIE = 'csrf-token';
  static TOKEN_STORAGE_KEY = 'csrf-token';
  
  static generateToken() {
    // Generate cryptographically secure random token
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }
  
  static initializeCSRFProtection() {
    try {
      // Generate and store CSRF token
      const token = this.generateToken();
      
      // Store in session storage (more secure than localStorage)
      sessionStorage.setItem(this.TOKEN_STORAGE_KEY, token);
      
      // Set as HttpOnly cookie (if backend supports it)
      document.cookie = `${this.TOKEN_COOKIE}=${token}; Secure; SameSite=Strict; Path=/`;
      
      // Intercept all requests to add CSRF token
      this.interceptRequests();
      
      console.log('✅ CSRF protection initialized');
    } catch (error) {
      console.error('❌ Failed to initialize CSRF protection:', error);
      // Don't fail the application, but log the error
    }
  }
  
  static interceptRequests() {
    const originalFetch = window.fetch;
    
    window.fetch = function(url, options = {}) {
      // Only add CSRF token to same-origin requests
      if (CSRFProtection.isSameOrigin(url)) {
        const token = sessionStorage.getItem(CSRFProtection.TOKEN_STORAGE_KEY);
        
        if (token) {
          options.headers = {
            ...options.headers,
            [CSRFProtection.TOKEN_HEADER]: token
          };
        }
      }
      
      return originalFetch.call(this, url, options);
    };
  }
  
  static isSameOrigin(url) {
    try {
      const requestUrl = new URL(url, window.location.origin);
      return requestUrl.origin === window.location.origin;
    } catch (error) {
      // Fail secure: if we can't determine origin, assume not same-origin
      return false;
    }
  }
  
  static validateRequest(request) {
    const token = sessionStorage.getItem(this.TOKEN_STORAGE_KEY);
    const requestToken = request.headers[this.TOKEN_HEADER];
    
    if (!token || !requestToken || token !== requestToken) {
      throw new Error('CSRF token validation failed');
    }
    
    return true;
  }
  
  static getToken() {
    return sessionStorage.getItem(this.TOKEN_STORAGE_KEY);
  }
}
